/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { loadUi, saveUi } from "../lib/uiMemory";
import CRMLeads from "./CRMLeads";
import SalesDashboard from "./SalesDashboard";
import OpsCockpit from "./OpsCockpit";
import WeekAgendaCalendar from "../components/WeekAgendaCalendar";
import { LayoutGrid, Target, Gauge } from "lucide-react";

export default function SalesWorkspace() {
  const { lang, user } = useApp();
  const zh = lang === "zh";
  const [sp, setSp] = useSearchParams();
  // 本组件同时服务 /crm/leads 与 /crm/leads/:id 两个路由（后者是线索详情的路径形态）。
  // 详情抽屉本身已由全局组件 GlobalLeadDrawer 统一渲染（挂一次、全站可用），
  // 这里只负责一件事：有 :id 时把标签锁到「客户看板」，保证抽屉背后就是线索列表。
  const { id: leadId } = useParams();
  const isOps = user.role === "sales_vp" || user.role === "sales_manager";
  const isSales = !isOps && (user.role === "sales" || !!user.salesName);
  const hasPerf = isOps || isSales;
  const valid = ["board", "perf"];
  const remembered = loadUi("crm_ws_tab", user.username);  // 锁定界面：记住上次停留的标签
  const init = leadId ? "board" : (valid.includes(sp.get("tab")) ? sp.get("tab") : (sp.get("consentTodo") === "1" ? "board" : valid.includes(remembered) ? remembered : hasPerf ? "perf" : "board"));
  const [tab, setTab] = useState(init === "perf" && !hasPerf ? "board" : init);
  // 已在看板页时点开某条线索（URL 从 /crm/leads?tab=board 变为 /crm/leads/:id），组件不会重新挂载、
  // 上面的 init 不会重算，故这里补一个副作用兜底把标签切回 board。
  useEffect(() => { if (leadId && tab !== "board") setTab("board"); }, [leadId]); // eslint-disable-line
  // 锁定界面：把当前标签按账号记住（需求：看完客户详情返回时回到上次那个界面，而不是跳回默认的「运营总监驾驶舱」）。
  // 关键点——无论从顶部标签点击进入，还是从 URL 直达（如铃铛通知 /crm/leads?tab=board、/sales 重定向 ?tab=perf），
  // 只要组件挂载就把当前 tab 写一次记忆，保证「详情返回」或「从菜单再进」都能复现同一界面。
  // 原实现只在 go()（点标签）里写 saveUi，URL 直达不写 → 记忆为空 → 回落到 hasPerf ? "perf" : "board"，
  // 销售总监/经理档 hasPerf=true 就跳回驾驶舱；这正是被报障的场景，故改为随 tab 变化的副作用统一写入。
  // 抽屉打开期间不写「上次停留的标签」：此时 tab 是被强制锁成 board 的，
  // 若照写会把用户真正的偏好（例如运营总监的 perf）覆盖掉。
  useEffect(() => { if (!leadId) saveUi("crm_ws_tab", user.username, tab); }, [tab, user.username, leadId]);
  const go = (t) => {
    setTab(t);
    const n = new URLSearchParams(sp);
    n.set("tab", t);
    setSp(n, { replace: true });
  };
  const tabs = [
    ...(isOps ? [{ k: "perf", label: zh ? "运营总监驾驶舱" : "Ops Cockpit", icon: Gauge }] : []),
    ...(isSales ? [{ k: "perf", label: zh ? "我的业绩" : "My Performance", icon: Target }] : []),
    { k: "board", label: zh ? "客户看板" : "Board", icon: LayoutGrid },
  ];
  return (
    <div data-testid="sales-workspace">
      <div className="page-pad-x page-bleed-x pt-2 -mt-4 sm:-mt-5 lg:-mt-6 mb-4 sm:mb-6 flex items-center gap-1 border-b border-slate-200/60 bg-white/90 backdrop-blur-md sticky below-breadcrumb z-10 overflow-x-auto" data-testid="sales-workspace-tabs">
        {tabs.map((t) => (
          <button
            key={t.k}
            data-testid={`workspace-tab-${t.k}`}
            onClick={() => go(t.k)}
            className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === t.k ? "border-brand-500 text-brand-500" : "border-transparent text-slate-500 hover:text-slate-800"
            }`)}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>
      {tab === "board" && <CRMLeads />}
      {tab === "perf" && isOps && <OpsCockpit />}
      {/* 页面外层已提供留白，手机再叠加24px会挤窄业绩与周日程。 */}
      {tab === "perf" && isSales && (
        <div className="space-y-4 sm:p-6 sm:space-y-6">
          <SalesDashboard embedded />
          <WeekAgendaCalendar embedded />
        </div>
      )}
    </div>
  );
}
